// University of Oregon: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - UO's Common Data Set 2025-2026 (CDS 2025-2026.pdf), the file the Office
//    of Data Enablement links from its Common Data Set page (the link opens a
//    UO SharePoint folder; the SOURCES url is the uoregon.edu page that links
//    it). C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table; C8 test policy for Fall 2027; C21/C22 plans; G1/G5 2026-2027 costs.
//  - UO Admissions: First-Year Students (deadlines, requirements, FAQs),
//    Test-Optional Policy, and Departmental Deadlines. The deadline table
//    carries no year; it is the page as posted for applications that opened
//    August 1, 2026 (the site banner reads "Early Action Deadline: November 1,
//    2026"). Quirk: CDS C22 lists an early action notification date of
//    December 15, while the admissions page says early action decisions come
//    on January 5; the page uses the admissions office's date.
//  - College Scorecard, school id 209551 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1 first-year column. UO's G1 note says the first-year
// tuition is the Duck Tuition Guarantee rate for students entering in 2026
// (a fixed rate for up to five years); the rate for students entering in
// fall 2027 is not yet in the CDS.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UO Common Data Set 2025-2026',
        url: 'https://data.uoregon.edu/uo-overview/common-data-set',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UO Admissions, First-Year Students',
        url: 'https://www.uoregon.edu/admissions/undergraduate/find-your-path/first-year-students/',
        read: '2026-10-01',
    },
    tests: {
        label: 'UO Admissions, Test-Optional Policy',
        url: 'https://www.uoregon.edu/admissions/undergraduate/find-your-path/first-year-students/test-optional-policy/',
        read: '2026-10-01',
    },
    departments: {
        label: 'UO Admissions, Departmental Deadlines',
        url: 'https://www.uoregon.edu/admissions/operations/departmental-deadlines-and-applications/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?209551-University-of-Oregon',
        read: '2026-10-15',
    },
    coa: {
        label: 'UO Financial Aid, 2026-27 Estimated Undergraduate Cost of Attendance',
        url: 'https://financialaid.uoregon.edu/cost_of_attendance',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'university-of-oregon',
    kbId: 'university_of_oregon',
    name: 'University of Oregon',
    shortName: 'Oregon',
    location: 'Eugene, OR',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (UO brand color page); decoration only.
    branding: {
        primary: '#007030',
        secondary: '#FEE11A',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_oregon_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 41368,
        admitted: 36148,
        enrolled: 5009,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 8018, admitted: 7064 },
            { group: 'Out-of-state', applied: 31873, admitted: 28417 },
            { group: 'International', applied: 1477, admitted: 667 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UO\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UO has no early decision plan. Its early action is not binding, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most majors take only the UO application. These programs review you separately and give their own decision; if UO admits you but the program does not, your major becomes "exploring":',
        rows: [
            { school: 'Clark Honors College', requirement: 'Answer the Clark Honors College prompt on the UO application, a second essay of up to 500 words, on the same early action and regular deadlines.', source: 'firstYear' },
            { school: 'Architecture and Interior Architecture', requirement: 'Two essay statements and a link to a creative portfolio, submitted with the UO application by November 1 (early action) or January 15.' },
            { school: 'Business', requirement: 'Choose Business as your intended major to be considered for direct admission. Most of the incoming business class starts as pre-business and applies to the major after the lower-division courses.' },
            { school: 'Music', requirement: 'A departmental application, due January 15, and an audition.' },
            { school: 'Product Design', requirement: 'No early action. A departmental application through SlideRoom by January 15, with a statement of interest, a portfolio, two references and a $5 fee.' },
        ],
        source: 'departments',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'January 5', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'By April 1', binding: false },
        ],
        note: 'Applications open August 1. Supporting documents are due November 15 for early action and February 15 for regular decision. Early action applicants are accepted, denied or deferred to regular decision. Everyone admitted has until May 1 to decide.',
        source: 'firstYear',
        binding: 'Early action is not binding: you still have until May 1 to decide. UO has no early decision program.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UO is test-optional: you can be considered for admission without SAT or ACT scores, and there is a check box on the application to say so. If you send scores, UO superscores the SAT or ACT, but says scores play little to no role in its review. Scores are not used for any merit or need-based scholarship.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [17475, 47466] },
            { item: 'Food and housing (on campus)', amounts: [18183, 18183] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1434, 1434] },
            { item: 'Personal expenses', amounts: [2673, 2673] },
            { item: 'Transportation', amounts: [471, 1500] },
        ],
        totals: [40236, 71256],
        note: 'New students also pay a one-time $570 matriculation fee, not included here.',
        source: 'coa',
        netPrice: {
            average: 22547,
            byIncome: [
                { income: '$0 to $30,000', amount: 15917 },
                { income: '$30,001 to $48,000', amount: 16118 },
                { income: '$48,001 to $75,000', amount: 18358 },
                { income: '$75,001 to $110,000', amount: 24089 },
                { income: 'Over $110,000', amount: 31631 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UO\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
